import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Filter, Sparkles, Clock } from "lucide-react";
import type {
  ReviewFilters,
  ReviewSort,
  ReviewStatusFilter,
  RecencyFilter,
} from "@/hooks/useReviewQueue";

const sb = supabase as any;

interface ExamLabel {
  id: string;
  name: string;
}
interface Topic {
  id: string;
  name: string;
}

interface ReviewFiltersBarProps {
  filters: ReviewFilters;
  onFiltersChange: (next: ReviewFilters) => void;
  sort: ReviewSort;
  onSortChange: (s: ReviewSort) => void;
  autoAdvance: boolean;
  onAutoAdvanceChange: (v: boolean) => void;
  autoRunAI: boolean;
  onAutoRunAIChange: (v: boolean) => void;
  className?: string;
}

export function ReviewFiltersBar({
  filters,
  onFiltersChange,
  sort,
  onSortChange,
  autoAdvance,
  onAutoAdvanceChange,
  autoRunAI,
  onAutoRunAIChange,
  className,
}: ReviewFiltersBarProps) {
  const [labels, setLabels] = useState<ExamLabel[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);

  useEffect(() => {
    sb.from("exam_labels")
      .select("id, name")
      .eq("is_active", true)
      .order("display_order", { ascending: true })
      .then(({ data }: any) => setLabels(data || []));
    sb.from("topics")
      .select("id, name")
      .order("name", { ascending: true })
      .limit(500)
      .then(({ data }: any) => setTopics(data || []));
  }, []);

  const update = (patch: Partial<ReviewFilters>) =>
    onFiltersChange({ ...filters, ...patch });

  return (
    <div className={className}>
      <div className="flex flex-wrap items-end gap-3">
        {/* Status */}
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <Filter className="h-3 w-3" /> Status
          </Label>
          <Select
            value={filters.status}
            onValueChange={(v) => update({ status: v as ReviewStatusFilter })}
          >
            <SelectTrigger className="h-8 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="needs_revision">Needs revision</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Exam */}
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Exam
          </Label>
          <Select
            value={filters.examLabelId ?? "all"}
            onValueChange={(v) =>
              update({ examLabelId: v === "all" ? null : v })
            }
          >
            <SelectTrigger className="h-8 w-40 text-xs">
              <SelectValue placeholder="All exams" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All exams</SelectItem>
              {labels.map((l) => (
                <SelectItem key={l.id} value={l.id}>
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Topic */}
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Topic
          </Label>
          <Select
            value={filters.topicId ?? "all"}
            onValueChange={(v) => update({ topicId: v === "all" ? null : v })}
          >
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue placeholder="All topics" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All topics</SelectItem>
              {topics.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Recency */}
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" /> Added
          </Label>
          <Select
            value={filters.recency}
            onValueChange={(v) => update({ recency: v as RecencyFilter })}
          >
            <SelectTrigger className="h-8 w-32 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any time</SelectItem>
              <SelectItem value="24h">Last 24h</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Sort */}
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Sort
          </Label>
          <Select value={sort} onValueChange={(v) => onSortChange(v as ReviewSort)}>
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="oldest_pending">Oldest first</SelectItem>
              <SelectItem value="ai_risk">AI risk: lowest confidence</SelectItem>
              <SelectItem value="newest">Newest first</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* AI risk only chip */}
        <Button
          type="button"
          size="sm"
          variant={filters.aiRiskOnly ? "default" : "outline"}
          onClick={() => update({ aiRiskOnly: !filters.aiRiskOnly })}
          className="h-8"
        >
          <Sparkles className="h-3.5 w-3.5 mr-1" />
          AI risk only
        </Button>

        <div className="ml-auto flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="auto-ai"
              checked={autoRunAI}
              onCheckedChange={onAutoRunAIChange}
            />
            <Label htmlFor="auto-ai" className="text-xs cursor-pointer">
              Auto-run AI
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="auto-advance"
              checked={autoAdvance}
              onCheckedChange={onAutoAdvanceChange}
            />
            <Label htmlFor="auto-advance" className="text-xs cursor-pointer">
              Auto-advance
            </Label>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Tiny pill that summarizes an AI verdict for queue rows. */
export function AiRiskPill({
  verdict,
  confidence,
}: {
  verdict: string;
  confidence: number;
}) {
  const tone =
    verdict === "approve" && confidence >= 70
      ? "bg-success/15 text-success border-success/30"
      : verdict === "needs_correction" || confidence < 50
      ? "bg-destructive/15 text-destructive border-destructive/30"
      : "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30";
  return (
    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${tone}`}>
      AI {confidence}%
    </Badge>
  );
}
